/**
 * Clipboard + save helpers for chat context menus. All best-effort: failures
 * log a warning and return false/empty rather than throwing at the caller.
 */
import { isTauri } from './tauri';

export async function copyTextToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text ?? '');
    return true;
  } catch (e) {
    console.warn('[clipboard] copy text failed:', e?.message || e);
    return false;
  }
}

/**
 * Copy an image to the clipboard from any fetchable src (blob: object URLs of
 * decrypted attachments included). Chromium clipboards only accept PNG, so
 * other formats are re-encoded via canvas.
 */
export async function copyImageToClipboard(src) {
  try {
    if (typeof ClipboardItem !== 'function') throw new Error('ClipboardItem unsupported');
    const blob = await (await fetch(src)).blob();
    let png = blob;
    if (blob.type !== 'image/png') {
      const bitmap = await createImageBitmap(blob);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = bitmap.width;
        canvas.height = bitmap.height;
        canvas.getContext('2d').drawImage(bitmap, 0, 0);
        png = await new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
      } finally {
        bitmap.close();
      }
    }
    if (!png) throw new Error('PNG encode failed');
    await navigator.clipboard.write([new ClipboardItem({ 'image/png': png })]);
    return true;
  } catch (e) {
    console.warn('[clipboard] copy image failed:', e?.message || e);
    return false;
  }
}

/**
 * Read the clipboard for pasting: prefers an image ({ image: Blob }) over
 * text ({ text }); resolves to {} when empty or unreadable.
 */
export async function readClipboard() {
  try {
    if (navigator.clipboard.read) {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) return { image: await item.getType(imageType) };
      }
      for (const item of items) {
        if (item.types.includes('text/plain')) {
          return { text: await (await item.getType('text/plain')).text() };
        }
      }
      return {};
    }
  } catch {
    // fall through to the plain-text API
  }
  try {
    return { text: await navigator.clipboard.readText() };
  } catch (e) {
    console.warn('[clipboard] paste failed:', e?.message || e);
    return {};
  }
}

/** Save an image src to disk — native dialog under Tauri, anchor download on the web. */
export async function downloadImage(src, filename = 'image') {
  try {
    const blob = await (await fetch(src)).blob();
    if (isTauri()) {
      const [{ save }, { writeFile }] = await Promise.all([
        import('@tauri-apps/plugin-dialog'),
        import('@tauri-apps/plugin-fs'),
      ]);
      const path = await save({ defaultPath: filename });
      if (path) await writeFile(path, new Uint8Array(await blob.arrayBuffer()));
    } else {
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
    }
    return true;
  } catch (e) {
    console.warn('[clipboard] image download failed:', e?.message || e);
    return false;
  }
}
